import {
  OverviewContext,
  OverviewGranularity,
  OverviewSurface,
  type NormalizedFinanceOverviewResponse,
} from "@/features/finance/model/finance-normalized-overview"
import {
  type FinanceAccountBalanceItem,
  type FinanceCategoryAccountBreakdown,
  type FinanceCategoryAccountBreakdownItem,
  type FinanceCurrentIncomeBalanceItem,
  type FinanceCurrentIncomeBalanceQuery,
  type FinanceCurrentIncomeBalanceResponse,
  type FinanceCurrentIncomeBalancesQuery,
  type FinanceCurrentIncomeBalancesResponse,
  type FinanceOverviewQuery,
} from "@/features/finance/model/finance-types"
import {
  http,
} from "@/shared/lib/api/http"
import {
  normalizeOverviewResponse,
} from "@/shared/lib/overview/normalize-overview-response"
import {
  ZERO_MONEY,
  extractListPayload,
  normalizeBoolean,
  normalizeCurrencyCode,
  normalizeMoney,
  normalizeNullableString,
  normalizeString,
} from "./finance-api-shared"


function normalizeFinanceOverviewResponse(input: unknown): NormalizedFinanceOverviewResponse {
  // No workspace-wide guess (IBA-114). The overview payload carries its own currency — the BRANCH's accounting
  // currency — and where it somehow does not, the component denominates it via useBranchCurrency. Reading a
  // workspace setting here was how a CDF branch got a dollar sign on its own money.
  return normalizeOverviewResponse(input)
}


function normalizeIncomeBalanceVisibility(raw: unknown): FinanceCurrentIncomeBalanceItem["visibility"] {
  if (raw === "BRANCH") return "BRANCH"
  if (raw === "GLOBAL" || raw === "SHARED") return "SHARED"
  return "SHARED"
}


function normalizeCurrentIncomeBalanceItem(input: unknown): FinanceCurrentIncomeBalanceItem {
  const record = (input ?? {}) as Record<string, unknown>
  return {
    incomeCategoryId: normalizeString(
      record.incomeCategoryId ??
        record.income_category_id ??
        record.incomeAccountId ??
        record.income_account_id,
    ) ?? "",
    incomeCategoryName: normalizeString(
      record.incomeCategoryName ??
        record.income_category_name ??
        record.incomeAccountName ??
        record.income_account_name,
    ) ?? "",
    incomeCategoryTranslationKey: normalizeNullableString(
      record.incomeCategoryTranslationKey ??
        record.income_category_translation_key ??
        record.incomeAccountTranslationKey ??
        record.income_account_translation_key,
    ),
    visibility: normalizeIncomeBalanceVisibility(record.visibility),
    currentBalance: normalizeMoney(record.currentBalance ?? record.current_balance) ?? ZERO_MONEY,
    spendableBalance: normalizeMoney(record.spendableBalance ?? record.spendable_balance) ?? ZERO_MONEY,
    totalIncome: normalizeMoney(record.totalIncome ?? record.total_income) ?? ZERO_MONEY,
    totalExpense: normalizeMoney(record.totalExpense ?? record.total_expense) ?? ZERO_MONEY,
    hasBalanceRow: record.hasBalanceRow === false || record.has_balance_row === false ? false : true,
  }
}


function normalizeCurrentIncomeBalanceAccountBreakdown(input: unknown): FinanceCurrentIncomeBalanceResponse["allowedAccounts"][number] {
  const record = (input ?? {}) as Record<string, unknown>
  return {
    accountId: normalizeString(record.accountId ?? record.account_id) ?? "",
    accountName: normalizeString(record.accountName ?? record.account_name) ?? "",
    branchId: normalizeString(record.branchId ?? record.branch_id) ?? "",
    status: normalizeString(record.status) === "ARCHIVED" ? "ARCHIVED" : "ACTIVE",
    isDefault: normalizeBoolean(record.isDefault ?? record.is_default),
    currentBalance: normalizeMoney(record.currentBalance ?? record.current_balance) ?? ZERO_MONEY,
    spendableBalance: normalizeMoney(record.spendableBalance ?? record.spendable_balance) ?? ZERO_MONEY,
    hasBalanceRow: record.hasBalanceRow === false || record.has_balance_row === false ? false : true,
  }
}


function normalizeCurrentIncomeBalanceResponse(
  input: unknown,
  query: FinanceCurrentIncomeBalanceQuery,
): FinanceCurrentIncomeBalanceResponse {
  const record = (input ?? {}) as Record<string, unknown>
  const item = normalizeCurrentIncomeBalanceItem(record)
  const hasBalanceRow = item.hasBalanceRow

  return {
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? query.workspaceId,
    branchId: normalizeString(record.branchId ?? record.branch_id) ?? query.branchId,
    // The scale of the money strings on this payload — sent by the API (IBA-116). Never defaulted to a currency.
    currency: normalizeCurrencyCode(record.currency) ?? null,
    incomeCategoryId: normalizeString(
      record.incomeCategoryId ??
        record.income_category_id ??
        record.incomeAccountId ??
        record.income_account_id,
    ) ?? query.incomeCategoryId,
    incomeCategoryName: item.incomeCategoryName,
    incomeCategoryTranslationKey: item.incomeCategoryTranslationKey,
    visibility: item.visibility,
    currentBalance: hasBalanceRow ? item.currentBalance : ZERO_MONEY,
    spendableBalance: hasBalanceRow ? item.spendableBalance : ZERO_MONEY,
    totalIncome: hasBalanceRow ? item.totalIncome : ZERO_MONEY,
    totalExpense: hasBalanceRow ? item.totalExpense : ZERO_MONEY,
    hasBalanceRow,
    defaultAccountId: normalizeNullableString(record.defaultAccountId ?? record.default_account_id),
    allowedAccounts: Array.isArray(record.allowedAccounts ?? record.allowed_accounts)
      ? ((record.allowedAccounts ?? record.allowed_accounts) as unknown[]).map(
          normalizeCurrentIncomeBalanceAccountBreakdown,
        )
      : [],
  }
}


export function buildOverviewParams(query: FinanceOverviewQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {
    workspaceId: query.workspaceId,
  }

  const context = query.context ?? OverviewContext.Branch
  params.context = context

  // A GLOBAL read has no single branch, and sending one would silently narrow the consolidated aggregate back
  // to one branch's numbers — a wrong answer that still looks like a right one. Explicit rather than relying on
  // `branchId` happening to be falsy at the call site.
  if (context !== OverviewContext.Global && query.branchId) {
    params.branchId = query.branchId
  }

  if (query.incomeCategoryId) params.incomeCategoryId = query.incomeCategoryId
  if (query.expenseCategoryId) params.expenseCategoryId = query.expenseCategoryId
  if (query.activityId) params.activityId = query.activityId
  if (query.groupId) params.groupId = query.groupId
  if (query.ministryId) params.ministryId = query.ministryId
  if (query.memberId) params.memberId = query.memberId

  if (query.dateFrom && query.dateTo) {
    params.dateFrom = query.dateFrom
    params.dateTo = query.dateTo
  }

  params.granularity = query.granularity ?? OverviewGranularity.Monthly
  params.profile = query.profile ?? OverviewSurface.Full

  const limit = query.limit ?? 10
  if (limit > 0) params.limit = Math.min(limit, 50)

  return params
}


function buildCurrentIncomeBalancesParams(query: FinanceCurrentIncomeBalancesQuery): Record<string, string> {
  const params: Record<string, string> = {
    workspaceId: query.workspaceId,
    branchId: query.branchId,
  }

  if (query.incomeCategoryId) {
    params.incomeCategoryId = query.incomeCategoryId
  }

  if (query.departmentId) {
    params.departmentId = query.departmentId
  }

  return params
}


export async function getFinanceOverview(query: FinanceOverviewQuery): Promise<NormalizedFinanceOverviewResponse> {
  const response = await http.get<unknown>("/finance/overview", {
    params: buildOverviewParams(query),
  })

  return normalizeFinanceOverviewResponse(response.data)
}


function normalizeAccountBalanceItem(input: unknown): FinanceAccountBalanceItem {
  const record = (input ?? {}) as Record<string, unknown>
  return {
    accountId: normalizeString(record.accountId ?? record.account_id) ?? "",
    accountName: normalizeString(record.accountName ?? record.account_name) ?? "",
    currentBalance: normalizeMoney(record.currentBalance ?? record.current_balance) ?? ZERO_MONEY,
  }
}


function normalizeCategoryAccountBreakdownItem(input: unknown): FinanceCategoryAccountBreakdownItem {
  const record = (input ?? {}) as Record<string, unknown>
  return {
    accountId: normalizeString(record.accountId ?? record.account_id) ?? "",
    accountName: normalizeString(record.accountName ?? record.account_name) ?? "",
    income: normalizeMoney(record.income) ?? ZERO_MONEY,
    expense: normalizeMoney(record.expense) ?? ZERO_MONEY,
    balance: normalizeMoney(record.balance) ?? ZERO_MONEY,
  }
}


function normalizeCategoryAccountBreakdown(input: unknown): FinanceCategoryAccountBreakdown {
  const record = (input ?? {}) as Record<string, unknown>
  const totals = (record.totals ?? {}) as Record<string, unknown>
  const items = Array.isArray(record.items) ? record.items.map(normalizeCategoryAccountBreakdownItem) : []

  return {
    incomeCategoryId: normalizeString(record.incomeCategoryId ?? record.income_category_id) ?? "",
    items,
    totals: {
      income: normalizeMoney(totals.income) ?? ZERO_MONEY,
      expense: normalizeMoney(totals.expense) ?? ZERO_MONEY,
      balance: normalizeMoney(totals.balance) ?? ZERO_MONEY,
    },
  }
}


/**
 * The Soldes payload, totals included. The client no longer re-derives ANY of these figures: the category total
 * row, the account balances and their total, and every per-category drill-down are all summed by the backend
 * over the whole branch (IBA-117).
 */
export async function getFinanceCurrentIncomeBalances(
  query: FinanceCurrentIncomeBalancesQuery,
): Promise<FinanceCurrentIncomeBalancesResponse> {
  const response = await http.get<unknown>("/finance/current-income-balances", {
    params: buildCurrentIncomeBalancesParams(query),
  })

  const record = (Array.isArray(response.data) ? {} : response.data ?? {}) as Record<string, unknown>
  const rawItems = Array.isArray(response.data)
    ? response.data
    : Array.isArray(record.items)
      ? record.items
      : []
  const items = rawItems.map(normalizeCurrentIncomeBalanceItem)

  const totals = (record.totals ?? {}) as Record<string, unknown>
  const accounts = (record.accounts ?? {}) as Record<string, unknown>
  const accountTotals = (accounts.totals ?? {}) as Record<string, unknown>

  return {
    workspaceId: normalizeString(record.workspaceId ?? record.workspace_id) ?? query.workspaceId,
    branchId: normalizeString(record.branchId ?? record.branch_id) ?? query.branchId,
    // The scale of every money string on this payload — sent by the API (IBA-116). Never defaulted to a currency.
    currency: normalizeCurrencyCode(record.currency) ?? null,
    items,
    totals: {
      totalIncome: normalizeMoney(totals.totalIncome ?? totals.total_income) ?? ZERO_MONEY,
      totalExpense: normalizeMoney(totals.totalExpense ?? totals.total_expense) ?? ZERO_MONEY,
      currentBalance: normalizeMoney(totals.currentBalance ?? totals.current_balance) ?? ZERO_MONEY,
    },
    accounts: {
      items: Array.isArray(accounts.items) ? accounts.items.map(normalizeAccountBalanceItem) : [],
      totals: {
        currentBalance: normalizeMoney(accountTotals.currentBalance ?? accountTotals.current_balance) ?? ZERO_MONEY,
      },
    },
    categoryAccountBreakdown: Array.isArray(record.categoryAccountBreakdown ?? record.category_account_breakdown)
      ? ((record.categoryAccountBreakdown ?? record.category_account_breakdown) as unknown[]).map(
          normalizeCategoryAccountBreakdown,
        )
      : [],
  }
}


/**
 * Per-department income / expense / net for a period (`GET /finance/departments/summary`).
 *
 * The department finance screen used to derive these by adding up `records` in the browser — but `records` is
 * one PAGE of transactions (100), so a department with more history than that displayed totals that were
 * silently too small. A total is the server's answer, computed over every transaction in the range.
 */
export type DepartmentFinanceSummaryQuery = {
  workspaceId: string
  branchId?: string | null
  dateFrom?: string
  dateTo?: string
}

/** One department's income / expense / net for the range. Every figure is the SERVER's total. */
export type DepartmentFinanceSummaryItem = {
  departmentId: string
  departmentName: string
  income: string
  expense: string
  net: string
}

export async function getDepartmentFinanceSummary(
  query: DepartmentFinanceSummaryQuery,
): Promise<DepartmentFinanceSummaryItem[]> {
  const response = await http.get<unknown>("/finance/departments/summary", {
    params: {
      workspaceId: query.workspaceId,
      ...(query.branchId ? { branchId: query.branchId } : {}),
      ...(query.dateFrom ? { dateFrom: query.dateFrom } : {}),
      ...(query.dateTo ? { dateTo: query.dateTo } : {}),
    },
  })

  return extractListPayload(response.data, ["items"], (value) => {
    const record = (value ?? {}) as Record<string, unknown>
    return {
      departmentId: normalizeString(record.departmentId ?? record.department_id) ?? "",
      departmentName: normalizeString(record.departmentName ?? record.department_name) ?? "",
      income: normalizeMoney(record.income) ?? ZERO_MONEY,
      expense: normalizeMoney(record.expense) ?? ZERO_MONEY,
      net: normalizeMoney(record.net) ?? ZERO_MONEY,
    }
  })
}


export async function getFinanceCurrentIncomeBalance(
  query: FinanceCurrentIncomeBalanceQuery,
): Promise<FinanceCurrentIncomeBalanceResponse> {
  const response = await http.get<unknown>("/finance/current-income-balance", {
    params: query,
  })

  const record = Array.isArray(response.data) ? response.data[0] : response.data
  return normalizeCurrentIncomeBalanceResponse(record, query)
}
